import React, { useState, useEffect, useMemo } from 'react';

import { Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, ROLE_CONFIG, ADMIN_MENUS } from './constants';

export function useGestionRoles({ getAuthHeader, user }) {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'system', 'custom'
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
    menus: [],
    color: '#6B7280'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const rolesRes = await axios.get(
        `${API}/api/roles/`,
        { headers: getAuthHeader() }
      );
      setRoles(rolesRes.data.roles || []);
      
      const permsRes = await axios.get(
        `${API}/api/roles/permissions`,
        { headers: getAuthHeader() }
      );
      setPermissions(permsRes.data.permissions || {});
      setGroupedPermissions(permsRes.data.grouped || {});
      
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!roleForm.name || !roleForm.display_name) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      await axios.post(
        `${API}/api/roles/`,
        roleForm,
        { headers: getAuthHeader() }
      );
      
      toast.success('Rôle créé avec succès');
      setShowRoleModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;

    try {
      await axios.put(
        `${API}/api/roles/${selectedRole.name}`,
        {
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          menus: roleForm.menus,
          color: roleForm.color
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Rôle mis à jour');
      setShowRoleModal(false);
      setSelectedRole(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm(`Supprimer le rôle "${roleName}" ? Cette action est irréversible.`)) return;

    try {
      await axios.delete(
        `${API}/api/roles/${roleName}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Rôle supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDuplicateRole = (role) => {
    setSelectedRole(null);
    setRoleForm({
      name: `${role.name}_copy`,
      display_name: `${role.display_name} (Copie)`,
      description: role.description || '',
      permissions: [...(role.permissions || [])],
      menus: [...(role.menus || [])],
      color: role.color || '#6B7280'
    });
    setShowRoleModal(true);
    toast.info('Rôle dupliqué - Modifiez et enregistrez');
  };

  const resetForm = () => {
    setRoleForm({
      name: '',
      display_name: '',
      description: '',
      permissions: [],
      color: '#6B7280'
    });
  };

  const openCreateModal = () => {
    resetForm();
    setSelectedRole(null);
    // Ouvrir toutes les catégories par défaut
    const allCategories = {};
    Object.keys(groupedPermissions).forEach(cat => {
      allCategories[cat] = true;
    });
    setExpandedCategories(allCategories);
    setShowRoleModal(true);
  };

  const openEditModal = (role) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      display_name: role.display_name,
      description: role.description || '',
      permissions: role.permissions || [],
      // Rôle ancien sans menus choisis : tous les menus étaient visibles, on les précoche.
      menus: Array.isArray(role.menus) ? role.menus : ADMIN_MENUS.map((m) => m.value),
      color: role.color || ROLE_CONFIG[role.name]?.color || '#6B7280'
    });
    // Ouvrir les catégories qui ont des permissions sélectionnées
    const expandedCats = {};
    Object.keys(groupedPermissions).forEach(cat => {
      const categoryPerms = groupedPermissions[cat]?.map(p => p.key) || [];
      if (categoryPerms.some(p => role.permissions?.includes(p))) {
        expandedCats[cat] = true;
      }
    });
    setExpandedCategories(expandedCats);
    setShowRoleModal(true);
  };

  const openPermissionsView = (role) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const togglePermission = (permKey) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const selectAllInCategory = (category) => {
    const categoryPerms = groupedPermissions[category]?.map(p => p.key) || [];
    const allSelected = categoryPerms.every(p => roleForm.permissions.includes(p));
    
    if (allSelected) {
      setRoleForm(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !categoryPerms.includes(p))
      }));
    } else {
      setRoleForm(prev => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...categoryPerms])]
      }));
    }
  };

  const selectAllPermissions = () => {
    const allPerms = Object.values(groupedPermissions).flat().map(p => p.key);
    setRoleForm(prev => ({
      ...prev,
      permissions: allPerms
    }));
  };

  const deselectAllPermissions = () => {
    setRoleForm(prev => ({
      ...prev,
      permissions: []
    }));
  };

  // Filtrage des rôles
  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      const matchesSearch = 
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.display_name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = 
        filterType === 'all' ||
        (filterType === 'system' && role.is_system) ||
        (filterType === 'custom' && !role.is_system);
      
      return matchesSearch && matchesFilter;
    });
  }, [roles, searchTerm, filterType]);

  // Statistiques
  const stats = useMemo(() => ({
    total: roles.length,
    system: roles.filter(r => r.is_system).length,
    custom: roles.filter(r => !r.is_system).length,
    totalPermissions: Object.keys(permissions).length,
    totalUsers: roles.reduce((sum, r) => sum + (r.user_count || 0), 0)
  }), [roles, permissions]);

  const getRoleConfig = (role) => ROLE_CONFIG[role.name] || {
    color: role.color || '#6B7280',
    bgColor: 'bg-gray-100',
    icon: Shield,
    label: role.display_name,
    description: role.description
  };

  const isSuperAdmin = user?.role === 'super_admin';

  return { deselectAllPermissions, expandedCategories, filterType, filteredRoles, getAuthHeader, getRoleConfig, groupedPermissions, handleCreateRole, handleDeleteRole, handleDuplicateRole, handleUpdateRole, isSuperAdmin, loadData, loading, openCreateModal, openEditModal, openPermissionsView, permissions, resetForm, roleForm, roles, searchTerm, selectAllInCategory, selectAllPermissions, selectedRole, showPermissionsModal, showRoleModal, stats, toggleCategory, togglePermission, user, viewMode, setFilterType, setSelectedRole, setLoading, setSearchTerm, setPermissions, setGroupedPermissions, setShowPermissionsModal, setRoles, setRoleForm, setShowRoleModal, setViewMode, setExpandedCategories };
}
